<template>
    <div class="registration-wrapper">
      
        <div class="registration-container">
      
        <RegistrationHeader
          title="Регистрация"
          :emitBack="true"
          @back="handleCancel"
        />

            <div class="progress-bar">
                <div 
                    class="progress-fill" 
                    :class="{ 'loading': isCompleting }" 
                    :style="{ width: progressWidth }"
                ></div>
            </div>

            <div class="registration-header">
                <p class="registration-text">
                    {{ currentStepText }}
                </p>

                <div class="registration-buttons">
                    <button v-if="currentStep === 1" class="cancel-button" @click="handleCancel">
                        Отмена
                    </button>
                    <button v-else class="back-button" @click="prevStep">
                        <img src="../assets/back-icon.png" alt="Назад" class="button-icon" />
                        Назад
                    </button>

                    <button class="next-button" 
                        @click="triggerNextStep"
                        :class="{ disabled: isCompleting || (currentStep === 1 && !isMainRegFormValid) }"
                        :disabled="isCompleting || (currentStep === 1 && !isMainRegFormValid)"
                    >
                        <span v-if="currentStep === 2 && isSkipCompany">Пропустить</span>
                        <span v-else-if="currentStep < totalSteps">Далее</span>
                        <span v-else>Завершить</span>
                        <img src="/forward-icon.png" alt="Вперед" class="button-icon" />
                    </button>
                </div>
            </div>

            <teleport to="body">
                <div v-if="isDeleting"
                    class="global-busy-overlay"
                    role="status"
                    aria-live="polite"
                    aria-busy="true">
                    <div class="logout-spinner"></div>
                    <p class="logout-text">Удаляем аккаунт…</p>
                </div>
            </teleport>

            <div class="registration-content">
                <transition name="slide-fade">
                <component 
                    :is="currentStepComponent"
                    ref="currentStepRef"
                    :is-employer="isEmployer"
                    @next-step="handleNextStep" 
                    @complete-step="handleCompleteStep"
                    @save-company="handleCompanyStepComplete"
                    @save-hashtags="nextStep"
                    @save-salary="nextStep"
                    @save-consultant="nextStep"
                    @save-city="completeRegistration"
                    @form-valid="updateMainRegValidity"
                    @start-saving-city="startSavingCity"
                    @stop-saving-city="stopSavingCity"
                />
                </transition>
            </div>
            
            <Footer />

            <ModalConfirm 
            :isVisible="isModalVisible" 
            @close="isModalVisible = false" 
            @confirm="handleDeleteAccount"  
            />

            <teleport to="body">
              <div v-if="isGlobalBusy" class="global-overlay">
                <div class="big-spinner"></div>
                <p class="overlay-text">Сохраняем данные…</p>
              </div>
            </teleport>

            <RegistrationAIResumeUploadModal
              v-if="!isEmployer"
              :is-visible="showResumeUploadModal"
              @complete="handleResumeParsed"
              @close="handleResumeModalClose"
            />

        </div>
    </div>
</template>

<script setup>

import RegistrationHeader from "../components/RegistrationHeader.vue";
import RegistrationAIResumeUploadModal from "../components/RegistrationAIResumeUploadModal.vue";

import Footer from "../components/MiniFooter.vue";

import MainReg from "../components/registration/MainReg.vue";
import ChooseCompany from "../components/registration/ChooseCompany.vue";
import Hashtags from "../components/registration/Hashtags.vue";
import Salary from "../components/registration/Salary.vue";
import Consultant from "../components/registration/Consultant.vue";
import ChooseCity from "../components/registration/ChooseCity.vue";

import ModalConfirm from "../components/ModalConfirm.vue";
import UserService from "../api/UserService.js";
import UserAuthService from "../api/UserAuthService.js";

import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { defineEmits } from "vue";

const showResumeUploadModal = ref(false);
const emit = defineEmits(["save-hashtags"]);
const router = useRouter();
const currentStep = ref(1);
const totalSteps = 6;
const isModalVisible = ref(false);
const steps = [MainReg, ChooseCompany, Hashtags, Salary, Consultant, ChooseCity];
const isLoading = ref(false);
const isSkipCompany = ref(false);
const isCompleting = ref(false);
const isGlobalBusy = ref(false);
const currentStepComponent = computed(() => steps[currentStep.value - 1] || MainReg);
const isMainRegFormValid = ref(false);
const currentStepRef = ref(null);
const dynamicOffset = ref(700);
const isEmployer = ref(false);
const CONSULTANT_STEP = 5;

function startSavingCity() {
    console.log("🚀 Начинаем сохранение города");
    isGlobalBusy.value = true;
}

function stopSavingCity() {
    console.log("ℹ stopSavingCity — игнорируем, ждём redirect");
}

const stepDescriptions = [
  "Основная информация поможет нам понять, кто вы и чем занимаетесь.",
  "Если вы уже работаете — укажите компанию. Это повысит доверие.",
  "Добавьте хэштеги, которые отражают ваши навыки и интересы. (необязательно)",
  "Укажите желаемую зарплату — это поможет найти подходящие предложения.",
  "Укажите, работали ли вы карьерным консультантом. Хотели ли бы вы что молодные начинающие свой путь специалисты могли обратиться к вам за помощью?",
  "Выберите город, в котором вы сейчас живёте или ищете работу."
];

const CONTENT_WIDTH = 1400;   
const PAGE_PADDING = 0;       

function recalcOffset() {
  const available = Math.max(0, window.innerWidth - PAGE_PADDING * 2);
  const container = Math.min(CONTENT_WIDTH, available);
  dynamicOffset.value = Math.round(container / 2);
}

const currentStepText = computed(() => {
  return stepDescriptions[currentStep.value - 1] || "";
});

const handleNextStep = () => {
  console.log("📩 Получено событие next-step из дочернего компонента");
  handleCompleteStep();
};

const triggerNextStep = () => {
  if (currentStep.value === 2 && isSkipCompany.value) {
    nextStep();
    return;
  }

  if (currentStepComponent.value === MainReg) {
    isLoading.value = true;
    if (currentStep.value === 1) {
      document.dispatchEvent(new CustomEvent("submit-main-reg"));
    } else {
      nextStep();
    }
  } else if (currentStepComponent.value === ChooseCompany) {
    if (currentStepRef.value?.updateUserCompany) {
      currentStepRef.value.updateUserCompany();
    } else {
      console.error("❌ Не найдена функция updateUserCompany");
    }
  } else if (currentStepComponent.value === Hashtags) {
    document.dispatchEvent(new CustomEvent("save-hashtags"));
  } else if (currentStepComponent.value === Salary) {
    document.dispatchEvent(new CustomEvent("save-salary"));
  } else if (currentStepComponent.value === Consultant) {
  console.log("🟡 Consultant шаг активен");
  console.log("🔎 currentStepRef:", currentStepRef.value);

  if (currentStepRef.value?.saveConsultant) {
    currentStepRef.value.saveConsultant();
    } else {
        console.error("❌ saveConsultant не найден — возможно, ref ещё не готов");
    }
  } else if (currentStepComponent.value === ChooseCity) {
    currentStepRef.value.updateCity();
  } else {
    nextStep();
  }
};

const handleCompanyStepComplete = (payload) => {
  console.log("🏢 Шаг компании завершён:", payload);
  console.log("👔 isEmployer:", isEmployer.value);

  if (isEmployer.value) {
    currentStep.value = CONSULTANT_STEP;
    return;
  }
  nextStep();
};

const handleCompleteStep = (payload) => {
  isLoading.value = false;

  if (currentStepComponent.value === MainReg) {
    isEmployer.value =
      payload?.isEmployer === true;

    currentStep.value = 2;

    if (isEmployer.value) {
      showResumeUploadModal.value = false;
      return;
    }

    setTimeout(() => {
      showResumeUploadModal.value = true;
    }, 250);

    return;
  }

  nextStep();
};

const updateMainRegValidity = (isValid) => {
    isMainRegFormValid.value = isValid;
};

const completeRegistration = async () => {
    try {
        await UserService.completeRegistration();
        setTimeout(() => {
            router.push("/feed");
        }, 2000);
    } catch (error) {
        console.error("❌ Ошибка при завершении регистрации:", error);
    }
};

const nextStep = () => {
    if (currentStep.value < totalSteps) currentStep.value++;
};

const prevStep = () => {
    if (currentStep.value > 1) currentStep.value--;
};

const progressWidth = computed(() => `${(currentStep.value / totalSteps) * 100}%`);

const handleCancel = () => {
  if (isDeleting.value) return;
  isModalVisible.value = true;
};

const isDeleting = ref(false);

const handleDeleteAccount = async () => {
  if (isDeleting.value) return;
  isModalVisible.value = false;

  try {
    isDeleting.value = true;

    await UserAuthService.deleteAccount();

    [
      "user_id","auth_token","emailForSignIn","user_password",
      "reg_email","reg_email_verified","reg_created_at","reg_disabled",
      "reg_success","reg_message","emailVerificationStartTimestamp",
    ].forEach((k) => localStorage.removeItem(k));

    router.push("/");
  } catch (error) {
    console.error("❌ Ошибка при удалении аккаунта:", error);
    isDeleting.value = false;
  }
};

onMounted(() => {
  recalcOffset();
  window.addEventListener("resize", recalcOffset, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", recalcOffset);
});

const handleResumeModalClose = () => {
  showResumeUploadModal.value = false;
};

const handleResumeParsed = (result) => {
  console.log("✅ Резюме обработано:", result);

  showResumeUploadModal.value = false;

  setTimeout(() => {
    currentStep.value = totalSteps;
  }, 250);
};

</script>

<style scoped>

.registration-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: var(--header-bg-dark);
    overflow: hidden;
}

[data-theme="light"] .registration-wrapper {
  background-color: var(--header-bg-light);
}

.registration-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    background-color: var(--header-bg-dark);
}

[data-theme="light"] .registration-container {
  background-color: var(--header-bg-light);
}

.registration-content {
    flex: 1;
    background-color: var(--header-bg-dark);
    display: flex;
    align-items: top;
    justify-content: left;
    margin: 15px 10%;
}

[data-theme="light"] .registration-content {
  background-color: var(--header-bg-light);
}

.progress-bar{

width:100%;
height:8px;

overflow:hidden;

position:relative;

background:#1E1E1E;

}

[data-theme="light"] .progress-bar{

background:#ECECEC;

}

.progress-fill{

position:relative;

height:100%;

border-radius:999px;

overflow:hidden;

background:
    linear-gradient(
        90deg,
        #2887F7,
        #4F46E5
    );

transition:
    width .55s cubic-bezier(.22,1,.36,1);

}

.progress-fill.loading {
    animation: loading-animation 1.5s ease-in-out forwards;
}

.progress-fill::before{

content:"";

position:absolute;

inset:0;

background:
    linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,.45),
        transparent
    );

animation:
    progressShine 2s linear infinite;

}

@keyframes loading-animation{
0%{
    opacity:1;
}
50%{
    opacity:.65;
}
100%{
    opacity:1;
}
}

@keyframes progressShine{
from{
    transform:translateX(-100%);
}
to{
    transform:translateX(220%);
}
}

.registration-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 15px 10%;
}

.registration-text {
    width: 600px;
    font-size: 14px;
    font-family: "Eastman-Regular", sans-serif;
    color: var(--header-text-dark);
    max-width: 60%;
    line-height: 1.4;
}

[data-theme="light"] .registration-text {
  color: var(--header-text-light);
}

.registration-buttons {
    display: flex;
    gap: 10px;
}

.button-icon{
    width:14px;
    height:14px;
    flex-shrink:0;
    object-fit:contain;
    transition:transform .2s ease;
}

.next-button:hover .button-icon{
    transform:translateX(3px);
}

.back-button:hover .button-icon{
    transform:translateX(-3px);
}

.next-button:hover{
box-shadow:
    0 0 0 4px rgba(40,135,247,.12),
    0 18px 40px rgba(40,135,247,.35);
}

.back-button,
.next-button,
.cancel-button {
    position: relative;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;

    min-width: 120px;
    height: 46px;

    padding: 0 22px;

    border: none;
    border-radius: 999px;

    cursor: pointer;

    font-size: 14px;
    font-family: "Eastman-Medium", sans-serif;

    transition:
        transform .22s ease,
        box-shadow .22s ease,
        background .25s ease,
        opacity .25s ease;

    overflow: hidden;
}

.back-button {

background: #3D3D3D;

color: white;

box-shadow:
    0 8px 18px rgba(0,0,0,.18);

}

[data-theme="light"] .back-button{

background:#ECECEC;
color:#333;

}

.next-button {

color:white;

background:
    linear-gradient(
        135deg,
        #2887F7,
        #4F46E5
    );

box-shadow:
    0 12px 30px rgba(40,135,247,.35);

}

.cancel-button{

background:
    linear-gradient(
        135deg,
        #E53935,
        #C62828
    );

color:white;

box-shadow:
    0 10px 24px rgba(229,57,53,.30);

}

.logout-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8); 
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    opacity: 0;
    animation: fadeIn 0.5s forwards;
}

.logout-spinner {
    width: 50px;
    height: 50px;
    border: 5px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.logout-text {
    margin-top: 15px;
    color: white;
    font-size: 16px;
    font-family: "Eastman-Regular", sans-serif;
    opacity: 0;
    animation: fadeIn 1s forwards 0.5s;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes fadeIn {
    to {
        opacity: 1;
    }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.global-busy-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100000; 
  opacity: 0;
  animation: fadeIn 0.3s forwards;
  pointer-events: all;
}

.logout-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.logout-text {
  margin-top: 15px;
  color: white;
  font-size: 16px;
  font-family: "Eastman-Regular", sans-serif;
  opacity: 0;
  animation: fadeIn 0.6s forwards 0.2s;
}

.back-button.disabled,
.cancel-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.global-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  z-index: 200000; /* выше всех */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s forwards;
}

.big-spinner {
  width: 60px;
  height: 60px;
  border: 6px solid rgba(255,255,255,0.25);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.overlay-text {
  margin-top: 20px;
  color: white;
  font-size: 17px;
  font-family: "Eastman-Regular", sans-serif;
  opacity: 0;
  animation: fadeIn 0.4s forwards 0.2s;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes fadeIn {
  to { opacity: 1; }
}

@media (max-width: 768px) {

/* основной контейнер */

.registration-content{
  margin: 20px;
  justify-content: center;
}

/* описание шага */

.registration-text{
  width: 100%;
  max-width: 100%;
  font-size: 13px;
}

/* header с кнопками */

.registration-header{
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  margin: 20px;
}

/* кнопки */

.registration-buttons{
  width: 100%;
  justify-content: space-between;
}

/* footer */

.registration-wrapper{
  overflow-y: auto;
}

}

.back-button:hover,
.next-button:hover,
.cancel-button:hover{

    transform:
        translateY(-2px);

    box-shadow:
        0 18px 34px rgba(0,0,0,.18);

}

.back-button:active,
.next-button:active,
.cancel-button:active{

    transform:
        translateY(1px)
        scale(.98);

}

[data-theme="light"] .back-button .button-icon {
  filter: brightness(0);
}

</style>